import { motion } from "framer-motion";
import { Users, TrendingUp, Clock3, ShieldCheck, ArrowRight } from "lucide-react";
import { openCalendly } from "@/integrations/calendly";

const solutions = [
  {
    icon: Users,
    title: "Get More Clients",
    desc: "Turn more of the buyer and seller inquiries you already receive into booked appointments and signed clients.",
  },
  {
    icon: TrendingUp,
    title: "Improve Your Sales Process",
    desc: "Create a clear, consistent path from first inquiry to closing so results do not depend on memory or luck.",
  },
  {
    icon: Clock3,
    title: "Save Your Team Time",
    desc: "Reduce repetitive follow-up work with practical process improvement and automation that fits how your team already operates.",
  },
  {
    icon: ShieldCheck,
    title: "Stop Losing Opportunities",
    desc: "Give every new inquiry a defined follow-up process so leads do not disappear simply because you were at a showing or a closing.",
  },
];

export default function Features() {
  return (
    <section id="solutions" data-testid="solutions-section" className="py-20 md:py-28 bg-[#0B0F19]">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            How iVIBES Helps Real Estate Professionals
          </h2>
          <p className="mt-4 text-lg text-slate-200 leading-relaxed">
            Consulting that starts with your business goals — not with technology. Here's what we
            help you do:
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-6">
          {solutions.map((s, i) => (
            <motion.div
              key={s.title}
              data-testid={`solution-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 transition-[border-color] duration-300 hover:border-white/25"
            >
              <div className="h-12 w-12 rounded-xl bg-cyan-500/10 text-cyan-300 flex items-center justify-center mb-5">
                <s.icon size={24} strokeWidth={1.75} />
              </div>
              <h3 className="font-heading font-bold text-xl text-white mb-2.5">{s.title}</h3>
              <p className="text-slate-300 text-base leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-12 text-center"
        >
          <button
            type="button"
            onClick={() => openCalendly()}
            data-testid="solutions-book-call-button"
            data-track="cta_book_call_solutions"
            className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(99,102,241,0.45)]"
          >
            Book Your Lead Flow Review
            <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
